import { FC, useEffect, useState } from 'react';
import { Button, Input, Modal, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useNotificationContext } from '@/features/notifications';
import { useRegenerateProc } from '@/shared/api';
import { useDataInvalidation } from '@/shared/hooks';
import { useThemeContext, themeConfig } from '@/shared/theme';
import { Col, Row } from '@/shared/ui';

const { Title } = Typography;

type RegenerateProcModalProps = {
  procId?: string;
  open: boolean;
  onCancel: () => void;
};

export const RegenerateProcModal: FC<RegenerateProcModalProps> = ({
  procId = '',
  open,
  onCancel,
}) => {
  const invalidate = useDataInvalidation(['procs', procId]);
  const navigate = useNavigate();
  const { theme } = useThemeContext();
  const { showNotification } = useNotificationContext();
  const [prompt, setPrompt] = useState<string>('');

  const { mutate: regenerateProc, isLoading } = useRegenerateProc();

  const handleRegenerate = () => {
    regenerateProc(
      {
        pathParams: { procId },
        body: { message: prompt },
      },
      {
        onSuccess: () => {
          showNotification({
            type: 'success',
            message: 'Proc regeneration started',
            description:
              'Your proc is being regenerated. This may take a few moments.',
          });
          onCancel();
          invalidate();
          navigate('/dashboard');
        },
        onError: () => {
          showNotification({
            type: 'error',
            message: 'Failed to regenerate proc',
            description:
              'There was an error starting the proc regeneration process',
          });
        },
      },
    );
  };

  useEffect(() => {
    if (!open) {
      setPrompt('');
    }
  }, [open]);

  return (
    <Modal
      data-testid="regenerate-proc-modal"
      title={
        <Col
          alignItems="flex-start"
          style={{ margin: '10px 0', width: '100%' }}
        >
          <Title level={2}>Regenerate proc using AI</Title>
        </Col>
      }
      destroyOnClose
      width={800}
      open={open}
      footer={null}
      style={{ maxWidth: '90%' }}
      onCancel={onCancel}
    >
      <label style={{ color: themeConfig[theme].colors.text }}>
        Describe how you want to modify the proc
      </label>
      <Input.TextArea
        data-testid="ai-prompt-input"
        disabled={isLoading}
        rows={5}
        maxLength={400}
        showCount={true}
        style={{ marginTop: 10 }}
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
      />
      <Row justifyContent="flex-end" style={{ marginTop: 50 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          type="primary"
          loading={isLoading}
          disabled={!prompt || isLoading}
          onClick={handleRegenerate}
        >
          Regenerate proc
        </Button>
      </Row>
    </Modal>
  );
};
